import type { WatchedFolderStatus } from '../shared/desktop-api';

/**
 * Job setup › Watched folder, in words a person reads at a glance. The coordinator's sentences are
 * careful and long (they also go to the Windows log and to support); the screen shows a few words,
 * and keeps the coordinator's own sentence one click away. Pure, so its tests run without a window.
 */

export type WatchedTone = 'on' | 'off' | 'wait' | 'problem';
export type WatchedLook = { tone: WatchedTone; label: string; reason: string | null };
export type PlainSentence = { text: string; detail: string | null };

/** The message of a refused call, without Electron's "Error invoking remote method …" wrapping. */
export const messageOf = (reason: unknown): string => {
  const raw = reason instanceof Error ? reason.message : typeof reason === 'string' ? reason : '';
  return raw
    .replace(/^Error invoking remote method '[^']+': /, '')
    .replace(/^(?:[A-Za-z]*Error): /, '')
    .trim();
};

/** The first sentence of a coordinator sentence, at most `limit` characters. */
export const firstSentence = (text: string, limit = 110): string => {
  const first = text.trim().split(/(?<=[.!?])\s+/, 1)[0] ?? '';
  return first.length <= limit ? first : `${first.slice(0, limit - 1).trimEnd()}…`;
};

const isNetworkPath = (folder: string | undefined) =>
  Boolean(folder && /^[\\/]{2}[^\\/]/.test(folder));

/**
 * Why a folder was refused, or cannot be used, in one short sentence. The coordinator's sentence
 * comes back as `detail` when the short one leaves something out.
 */
// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
export const plainRefusal = (message: string, folder?: string, choosing = false): PlainSentence => {
  const said = message.trim();
  const short = (text: string): PlainSentence => ({
    text,
    detail: said && said !== text ? said : null,
  });
  if (/export folder/i.test(said) && /watched folder/i.test(said))
    return short('Exported files go here. Pick a different folder.');
  if (/another Windows user/i.test(said))
    return short('That folder belongs to another Windows user.');
  // A folder the person could see when they picked it and Paige cannot find is on a drive
  // letter only that person has: a mapped drive. A folder already chosen that has gone is just gone.
  if (
    /mapped drive/i.test(said) ||
    (choosing && /drive letter/i.test(said) && !isNetworkPath(folder))
  )
    return short("Mapped drives don't work here. Use the network path, like \\\\server\\share.");
  if (
    /network location is not answering/i.test(said) ||
    (isNetworkPath(folder) && /cannot reach|denied access|cannot find/i.test(said))
  )
    return short("Paige can't reach this network folder.");
  if (/already watched for another job/i.test(said))
    return short('Another job already watches this folder.');
  if (/inside, or contains|cannot overlap/i.test(said))
    return short("This overlaps another job's watched folder.");
  // California Screw, 2026-10-15: files other people and scanners put there, which Paige may not move.
  if (/can't (?:read or )?move files/i.test(said))
    return short("Paige isn't allowed to move files others put here.");
  if (/whole drive/i.test(said)) return short('Pick a folder, not a whole drive.');
  if (/Paige's own data folder/i.test(said))
    return short("That's Paige's own folder. Pick another.");
  if (/full path/i.test(said)) return short('Pick the folder again.');
  if (/could not confirm that you can open/i.test(said)) return short('Pick the folder again.');
  if (/path is a file/i.test(said)) return short("That's a file, not a folder.");
  if (/cannot find this folder|cannot be found/i.test(said)) return short('Folder not found.');
  if (
    /cannot write|cannot remove|cannot create|cannot add or remove|cannot change files/i.test(said)
  )
    return short("Paige can't add or remove files there.");
  if (/not allowed to see|cannot see what is in|cannot open that folder|denied access/i.test(said))
    return short("Paige isn't allowed to open this folder.");
  if (/no longer exists/i.test(said)) return short('This job no longer exists.');
  if (/no watched folder/i.test(said)) return short('Choose a folder first.');
  return short(firstSentence(said) || "That didn't work. Try again.");
};

/** A set-aside file's reason (the first line of its `.reason.txt`), in a few words. */
export const plainSetAsideReason = (reason: string | null): string => {
  const said = reason?.trim() ?? '';
  if (!said) return 'No reason saved';
  if (/reads PDF files and images|another kind/i.test(said)) return 'Not a PDF or image';
  if (/larger than/i.test(said)) return 'Too large';
  if (/\bempty\b/i.test(said)) return 'Empty file';
  if (/password|encrypted/i.test(said)) return 'Password-protected';
  if (/tried to take this file \d+ times/i.test(said)) return 'Kept failing';
  if (/damaged|not a valid|could not read|cannot be read|unreadable/i.test(said))
    return "Couldn't be read";
  return firstSentence(said, 60);
};

/** The status as a dot and one to three words. Paused and Not running are quiet; the rest need someone. */
export const watchedLook = (
  status: WatchedFolderStatus | null | undefined,
  enabled: boolean,
): WatchedLook => {
  if (!enabled || status?.state === 'disabled')
    return { tone: 'off', label: 'Paused', reason: null };
  const reason = status?.reason ? firstSentence(status.reason) : null;
  // California Screw, 2026-10-15: a file Windows will not let Paige move needs a person, whatever else the folder does.
  if (status?.cannotTake && status.state !== 'unavailable')
    return { tone: 'problem', label: "Can't take files", reason: status.cannotTake.reason };
  switch (status?.state) {
    case 'watching':
      return { tone: 'on', label: 'Watching', reason: null };
    case 'waiting':
      return { tone: 'wait', label: 'Waiting', reason };
    case 'unavailable':
      return {
        tone: 'problem',
        label: "Can't reach folder",
        reason: reason ? plainRefusal(status.reason ?? '', status.path).text : null,
      };
    default:
      return { tone: 'off', label: 'Not running', reason: reason ?? null };
  }
};

/**
 * The end of a folder's path, enough to tell it apart: "Scans\Invoices In" for D:\Scans\Invoices In.
 * Two parts, because an export folder is usually named after its job, and one part says the job twice.
 * The drive or server is left off; the whole path is shown once, under the route.
 */
export const folderName = (folder: string): string => {
  const parts = folder
    .replace(/[\\/]+$/, '')
    .split(/[\\/]/)
    .filter(Boolean);
  const tail = parts.slice(1).slice(-2);
  return tail.length > 0 ? tail.join('\\') : (parts.at(-1) ?? folder);
};
